type AnalyticsParamValue = string | number | boolean;

export type AnalyticsContext = {
  tenantId?: string;
  landingId?: string;
  userId?: string;
};

export type AnalyticsEventName =
  | "signup_started"
  | "signup_completed"
  | "generate_lead"
  | "landing_cta_clicked"
  | "landing_section_viewed"
  | "lead_form_started"
  | "lead_form_submitted"
  | "store_link_copied"
  | "store_preview_link_copied"
  | "store_link_shared"
  | "store_opened";

type MetaEventMapping =
  | { kind: "standard"; name: string; dedupeBy?: string }
  | { kind: "custom"; name: string; dedupeBy?: string }
  | { kind: "skip" };

const META_EVENTS: Record<AnalyticsEventName, MetaEventMapping> = {
  signup_started: { kind: "custom", name: "SignupStarted" },
  signup_completed: {
    kind: "standard",
    name: "CompleteRegistration",
    dedupeBy: "user_id",
  },
  generate_lead: { kind: "standard", name: "Lead" },
  landing_cta_clicked: { kind: "custom", name: "LandingCtaClicked" },
  landing_section_viewed: { kind: "skip" },
  lead_form_started: { kind: "standard", name: "Contact" },
  lead_form_submitted: { kind: "standard", name: "Lead" },
  store_link_copied: { kind: "skip" },
  store_preview_link_copied: { kind: "skip" },
  store_link_shared: { kind: "skip" },
  store_opened: { kind: "skip" },
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

let activeContext: AnalyticsContext = {};

export function setAnalyticsContext(context: AnalyticsContext): void {
  activeContext = { ...activeContext, ...context };
}

const loggedAnalyticsFailures = new Set<string>();

function reportAnalyticsFailure(source: string, err: unknown): void {
  if (loggedAnalyticsFailures.has(source)) return;
  loggedAnalyticsFailures.add(source);
  console.error(`analytics ${source} dispatch failed`, err);
}

export function trackEvent(
  name: AnalyticsEventName,
  params: Record<string, AnalyticsParamValue> = {},
): void {
  if (typeof window === "undefined") return;
  const enriched: Record<string, AnalyticsParamValue> = { ...params };
  if (activeContext.tenantId) enriched.tenant_id = activeContext.tenantId;
  if (activeContext.landingId) enriched.landing_id = activeContext.landingId;
  if (typeof window.gtag === "function") {
    try {
      window.gtag("event", name, enriched);
    } catch (err) {
      reportAnalyticsFailure("gtag", err);
    }
  }
  if (typeof window.clarity === "function") {
    try {
      window.clarity("event", name);
    } catch (err) {
      reportAnalyticsFailure("clarity", err);
    }
  }
  const meta = META_EVENTS[name] as MetaEventMapping | undefined;
  if (meta && meta.kind !== "skip") {
    const eventID = metaEventId(meta, enriched);
    const command = meta.kind === "standard" ? "track" : "trackCustom";
    dispatchToMeta(() => window.fbq?.(command, meta.name, enriched, { eventID }));
  }
}

const MAX_PENDING_META_EVENTS = 20;
let pendingMetaEvents: Array<() => void> = [];

function dispatchToMeta(send: () => void): void {
  if (typeof window.fbq === "function") {
    try {
      send();
    } catch (err) {
      reportAnalyticsFailure("fbq", err);
    }
    return;
  }
  if (pendingMetaEvents.length >= MAX_PENDING_META_EVENTS) return;
  pendingMetaEvents.push(send);
}

export function flushMetaEvents(): void {
  const pending = pendingMetaEvents;
  pendingMetaEvents = [];
  pending.forEach((send) => {
    try {
      send();
    } catch (err) {
      reportAnalyticsFailure("fbq", err);
    }
  });
}

export function trackMetaPageView(): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq("track", "PageView");
  } catch (err) {
    reportAnalyticsFailure("fbq", err);
  }
}

function metaEventId(
  meta: Extract<MetaEventMapping, { name: string }>,
  params: Record<string, AnalyticsParamValue>,
): string {
  if (!meta.dedupeBy) return newEventId();
  const identity = params[meta.dedupeBy];
  if (typeof identity === "string" || typeof identity === "number") {
    return `${meta.name}:${identity}`;
  }
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      `${meta.name} needs "${meta.dedupeBy}" to deduplicate and it is missing, falling back to a per-send id`,
    );
  }
  return newEventId();
}

function newEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
